import { useState, useEffect, useCallback } from 'react';
import { warn } from '../utils/logger';

// Analytics Hook for tracking user behavior
export const useAnalytics = () => {
  const [sessionId] = useState(() => {
    // Generate or retrieve session ID
    let id = localStorage.getItem('analytics_session_id');
    if (!id) {
      id = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('analytics_session_id', id);
    }
    return id;
  });

  // Track page view
  const trackPageView = useCallback(async (page, pageTitle, loadTime) => {
    try {
      const response = await fetch('/api/analytics/page-view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          page,
          pageTitle,
          loadTime,
          referrer: document.referrer
        })
      });

      if (!response.ok) {
        const errorMsg = await response.text().then(text => {
          try {
            const data = JSON.parse(text);
            return data.error?.message || data.error || 'Failed to track page view';
          } catch {
            return 'Failed to track page view';
          }
        }).catch(() => 'Failed to track page view');
        warn('Failed to track page view', { error: errorMsg, status: response.status });
      }
    } catch (err) {
      warn('Error tracking page view', { error: err.message, stack: err.stack });
    }
  }, [sessionId]);

  // Track user action
  const trackUserAction = useCallback(async (action, element, value, page) => {
    try {
      const response = await fetch('/api/analytics/user-action', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          action,
          element,
          value,
          page: page || window.location.pathname
        })
      });

      if (!response.ok) {
        const errorMsg = await response.text().then(text => {
          try {
            const data = JSON.parse(text);
            return data.error?.message || data.error || 'Failed to track user action';
          } catch {
            return 'Failed to track user action';
          }
        }).catch(() => 'Failed to track user action');
        warn('Failed to track user action', { error: errorMsg, status: response.status });
      }
    } catch (err) {
      warn('Error tracking user action', { error: err.message, stack: err.stack });
    }
  }, [sessionId]);

  // Track card generation
  const trackCardGeneration = useCallback(async (category, cardStyle, success, duration, error, fileSize, fileType, creditsUsed) => {
    try {
      const response = await fetch('/api/analytics/card-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          category,
          cardStyle,
          success,
          duration,
          error,
          fileSize,
          fileType,
          creditsUsed
        })
      });

      if (!response.ok) {
        const errorMsg = await response.text().then(text => {
          try {
            const data = JSON.parse(text);
            return data.error?.message || data.error || 'Failed to track card generation';
          } catch {
            return 'Failed to track card generation';
          }
        }).catch(() => 'Failed to track card generation');
        warn('Failed to track card generation', { error: errorMsg, status: response.status });
      }
    } catch (err) {
      warn('Error tracking card generation', { error: err.message, stack: err.stack });
    }
  }, [sessionId]);

  // Track business event
  const trackBusinessEvent = useCallback(async (event, amount, currency, product) => {
    try {
      const response = await fetch('/api/analytics/business-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          event,
          amount,
          currency,
          product
        })
      });

      if (!response.ok) {
        const errorMsg = await response.text().then(text => {
          try {
            const data = JSON.parse(text);
            return data.error?.message || data.error || 'Failed to track business event';
          } catch {
            return 'Failed to track business event';
          }
        }).catch(() => 'Failed to track business event');
        warn('Failed to track business event', { error: errorMsg, status: response.status });
      }
    } catch (err) {
      warn('Error tracking business event', { error: err.message, stack: err.stack });
    }
  }, [sessionId]);

  // Track generic event
  const trackEvent = useCallback(async (eventType, eventData, page, category, success, duration, error) => {
    try {
      const response = await fetch('/api/analytics/event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          eventType,
          eventData,
          page: page || window.location.pathname,
          category,
          success,
          duration,
          error
        })
      });

      if (!response.ok) {
        const errorMsg = await response.text().then(text => {
          try {
            const data = JSON.parse(text);
            return data.error?.message || data.error || 'Failed to track event';
          } catch {
            return 'Failed to track event';
          }
        }).catch(() => 'Failed to track event');
        warn('Failed to track event', { error: errorMsg, status: response.status });
      }
    } catch (err) {
      warn('Error tracking event', { error: err.message, stack: err.stack });
    }
  }, [sessionId]);

  return {
    sessionId,
    trackPageView,
    trackUserAction,
    trackCardGeneration,
    trackBusinessEvent,
    trackEvent
  };
};

// Hook for tracking page views automatically
export const usePageTracking = (pageName, pageTitle) => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    const startTime = Date.now();
    
    // Track page view after component mounts
    const timer = setTimeout(() => {
      const loadTime = Date.now() - startTime;
      trackPageView(pageName, pageTitle, loadTime);
    }, 100);

    return () => clearTimeout(timer);
  }, [pageName, pageTitle, trackPageView]);
};

// Hook for tracking button clicks
export const useClickTracking = (elementName, page) => {
  const { trackUserAction } = useAnalytics();

  const handleClick = useCallback((event) => {
    const value = event.target.value || event.target.textContent || '';
    trackUserAction('click', elementName, value, page);
  }, [trackUserAction, elementName, page]);

  return handleClick;
};

// Hook for tracking form interactions
export const useFormTracking = (formName, page) => {
  const { trackUserAction } = useAnalytics();

  const trackFormEvent = useCallback((eventType, fieldName, value) => {
    trackUserAction(eventType, `${formName}_${fieldName}`, value, page);
  }, [trackUserAction, formName, page]);

  return trackFormEvent;
};

// Performance tracking hook
export const usePerformanceTracking = () => {
  const { trackEvent } = useAnalytics();

  const trackPerformance = useCallback(async (metricName, value, metadata = {}) => {
    await trackEvent('performance', {
      metric: metricName,
      value,
      ...metadata
    });
  }, [trackEvent]);

  return trackPerformance;
};

// Error tracking hook
export const useErrorTracking = () => {
  const { trackEvent } = useAnalytics();

  const trackError = useCallback(async (error, context = {}) => {
    // Safely handle error parameter
    if (!error) return;

    await trackEvent('error', {
      message: error.message || String(error),
      stack: error.stack || '',
      ...context
    }, window.location.pathname, null, false, null, error.message || String(error));
  }, [trackEvent]);

  return { trackError };
};

export default useAnalytics;

